import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { ListOrdersQuery, Payment, ProofView, RefundEntry } from '@restaurant/api-client';
import { useQuery } from '@tanstack/react-query';
import { orderKeys } from './queryKeys';

export type OrderFilters = ListOrdersQuery;

export function useOrders(filters: OrderFilters = {}) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';

  return useQuery({
    queryKey: orderKeys.list(restaurantId, filters),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/orders', {
          params: { path: { restaurantId }, query: { limit: 50, ...filters } },
        }),
      );
    },
    enabled: !!restaurantId,
    // SSE (useOrdersRealtime) pushes an instant refetch when an order is created
    // or changes. This poll is a backstop for when the stream is briefly down
    // (server restart, dropped EventSource, cross-pod miss) so a new storefront
    // order still surfaces within ~20s instead of never - the live view must not
    // depend solely on the socket being healthy.
    refetchInterval: 20_000,
    // ...but the poll only ticks while the app is on screen, and a phone in a
    // pocket freezes it along with the socket. Every order that lands in that
    // window is missed by BOTH, and SSE has no replay - so the wake-up itself
    // has to re-ask. See orders-blackout.spec.ts (an order stayed invisible for
    // 80 minutes because nothing did).
    refetchOnWindowFocus: true,
    // And no freshness grace: leaving the Orders tab unmounts the board, so
    // coming back is a fresh mount that the app-wide 5-minute staleTime would
    // answer straight from cache - showing a list from before she walked away,
    // with nothing to say it is old. A live work queue re-reads on every entry.
    staleTime: 0,
  });
}

/**
 * Count of orders awaiting an owner decision (PENDING) - drives the red badge
 * on the Orders tab, mirroring `usePendingReservations`. Polls every 2 min so
 * the badge stays live from any screen.
 */
export function usePendingOrdersCount() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';

  return useQuery({
    queryKey: orderKeys.pendingCount(restaurantId),
    queryFn: async () => {
      const page = unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/orders', {
          params: { path: { restaurantId }, query: { status: 'PENDING', limit: 50 } },
        }),
      );
      return page.items.length;
    },
    enabled: !!restaurantId,
    refetchInterval: 1000 * 60 * 2,
    // The badge is the ONLY orders signal reachable from another tab, so it is
    // what has to be right the moment she picks the phone up. `staleTime: 0`
    // because the app-wide default (5 min) would let a wake-up decide the count
    // is still fresh and skip the refetch entirely. The wake-up that drives it
    // is installAppResume (utils/appResume.ts) - this option alone never fires
    // in the native shell, which is how the blackout survived the first fix.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/**
 * Live "situation" counts for the orders workspace header: orders that need an
 * owner decision (PENDING) and orders in progress (CONFIRMED). Real grouped
 * counts from the backend - not a capped list page - so they stay correct at
 * any volume. Polls so the strip stays live while the owner triages.
 */
export function useOrderSummary() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';

  return useQuery({
    queryKey: orderKeys.summary(restaurantId),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/orders/summary', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
    refetchInterval: 1000 * 60,
    // Same wake-up contract as the board it sits above: refetch on focus, and
    // no freshness grace - the app-wide 5-minute staleTime would otherwise let
    // a short sleep return to a header still counting the situation she left.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useOrderDetail(orderId: string) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';

  return useQuery({
    queryKey: orderKeys.detail(restaurantId, orderId),
    queryFn: async () => {
      return unwrapResult(
        await openapiClient.GET('/api/restaurants/{restaurantId}/orders/{orderId}', {
          params: { path: { restaurantId, orderId } },
        }),
      );
    },
    enabled: !!restaurantId && !!orderId,
  });
}

export function useOrderPayment(orderId: string) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';

  return useQuery({
    queryKey: [...orderKeys.detail(restaurantId, orderId), 'payment'],
    queryFn: async () => {
      return unwrapResult<Payment>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/orders/{orderId}/payment', {
          params: { path: { restaurantId, orderId } },
        }),
      );
    },
    enabled: !!restaurantId && !!orderId,
  });
}

/**
 * The customer's uploaded payment proof (manual bank-transfer rail) as a
 * short-lived presigned URL + metadata, or null when none was uploaded. Lets the
 * owner eyeball the transfer screenshot before accepting/rejecting. The signed
 * URL expires, so this refetches on mount rather than caching indefinitely.
 */
export function useOrderProof(orderId: string, enabled = true) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';

  return useQuery({
    queryKey: [...orderKeys.detail(restaurantId, orderId), 'proof'],
    queryFn: async () => {
      return unwrapResult<ProofView>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/orders/{orderId}/proof', {
          params: { path: { restaurantId, orderId } },
        }),
      );
    },
    enabled: enabled && !!restaurantId && !!orderId,
  });
}

/**
 * Refund history for an order - each entry carries a `settlementStatus`
 * (PENDING vs SETTLED) so the inspector can show a "confirm settled" action for
 * out-of-band refunds. Shares the order detail key prefix, so any
 * `orderKeys.all` invalidation (create/settle refund) refetches it.
 */
export function useOrderRefunds(orderId: string) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';

  return useQuery({
    queryKey: [...orderKeys.detail(restaurantId, orderId), 'refunds'],
    queryFn: async () => {
      return unwrapResult<RefundEntry[]>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/orders/{orderId}/refunds', {
          params: { path: { restaurantId, orderId } },
        }),
      );
    },
    enabled: !!restaurantId && !!orderId,
  });
}
